/**
 * ATTEST-UI-1 screen 2 — the allow/deny preview, in plain language.
 *
 * Shown before anything is drafted. A CPA should be able to tell a lender what
 * this letter can and cannot say *before* spending time writing statements that
 * will be refused. That is the "we understood the limits" signal the brief asks
 * for, and it is cheaper than discovering the limit at finalize time.
 *
 * The type names assertion ids; the policy carries the human text. Type-local
 * blocks (`additional_denied_assertions`) exist only in the type's own YAML, so
 * the policy lookup misses them — those render with the id humanised and a note
 * that the full rationale arrives with the refusal if a statement trips it.
 *
 * Bundle 2 item 1 changed the weighting, not the content. The two lists used to
 * sit side by side in equal columns, which made the first thing a CPA saw on
 * this screen a red panel of everything they may not do — an accusation before
 * they had typed anything. The allow list is now the full-width lead, because it
 * is the answer to the question they actually arrived with ("what can I get out
 * of this?"), and the deny list is one click below it.
 *
 * Collapsing it changes nothing about enforcement: the deny list is applied by
 * the engine on every draft and every build, whether or not this expander has
 * ever been opened, and the summary says so in as many words. It is a native
 * `<details>`, so the rationales and permitted alternatives stay in the DOM for
 * Ctrl-F and for a screen reader's browse mode — hidden from the eye, not from
 * the reader.
 */
import { useMemo } from 'react'
import type {
  AllowedAssertionSpec,
  AttestPolicy,
  DeniedAssertionSpec,
  LetterTypeSummary,
} from '@/components/attest/api'
import { WIZARD_FIELD_HELP } from '@/components/attest/wizardHelp'
import { HelpDot } from '@/components/ui/help-dot'
import { Badge } from '@/components/ui/badge'

/** `HISTORICAL_AMOUNT_AS_REPORTED` → `Historical amount as reported`. */
function humanise(id: string): string {
  const words = id.toLowerCase().replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export interface AssertionPreviewProps {
  letterType: LetterTypeSummary
  policy: AttestPolicy | null
}

export function AssertionPreview({ letterType, policy }: AssertionPreviewProps) {
  const allowedById = useMemo(() => {
    const map = new Map<string, AllowedAssertionSpec>()
    policy?.allowed_assertions?.forEach(a => map.set(a.id, a))
    return map
  }, [policy])

  const deniedById = useMemo(() => {
    const map = new Map<string, DeniedAssertionSpec>()
    policy?.denied_assertions?.forEach(d => map.set(d.id, d))
    return map
  }, [policy])

  return (
    <div data-testid="assertion-preview" className="space-y-4">
      <section className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4">
        <header className="flex items-center justify-between gap-2">
          {/* One "(i)" for the pair. The copy explains the allow list and the
              deny list together, and the same sentence twice reads as two
              different rules. */}
          <h3 className="flex items-center gap-1 text-sm font-semibold text-emerald-900">
            This letter may state
            <HelpDot {...WIZARD_FIELD_HELP.allowedStatements} testId="info-allowedStatements" />
          </h3>
          <Badge variant="outline">{letterType.allowed_assertions.length}</Badge>
        </header>
        {/* Full width now, so a long allow list would otherwise be one tall
            column of narrow cards. Two up from `md`; single column on a phone,
            where side-by-side cards would be unreadable. */}
        <ul className="mt-3 grid gap-3 md:grid-cols-2" data-testid="allowed-assertions">
          {letterType.allowed_assertions.map(id => {
            const spec = allowedById.get(id)
            return (
              <li key={id} className="rounded border border-emerald-200 bg-white p-3">
                <p className="text-sm font-medium text-emerald-900">
                  {spec?.label ?? humanise(id)}
                </p>
                <p className="mt-0.5 font-mono text-[11px] text-emerald-700">{id}</p>
                {spec?.guidance && (
                  <p className="mt-2 text-sm text-gray-700">{spec.guidance}</p>
                )}
                {spec?.requires_evidence && (
                  <p className="mt-2 text-xs font-medium text-emerald-800">
                    Requires a cited source before it can be issued.
                  </p>
                )}
              </li>
            )
          })}
        </ul>
      </section>

      <details
        data-testid="denied-assertions-expander"
        className="rounded-lg border border-red-200 bg-red-50/50 p-4"
      >
        {/* `list-none` plus our own caret: the native marker is the one element
            here Tailwind cannot colour, and a black triangle was the only thing
            on the card outside the palette. */}
        <summary className="flex cursor-pointer list-none items-center gap-2 [&::-webkit-details-marker]:hidden">
          <span aria-hidden className="text-red-700">▸</span>
          <h3 className="text-sm font-semibold text-red-900">This letter may never state</h3>
          <Badge variant="destructive">{letterType.denied_assertions.length}</Badge>
        </summary>
        {/* Said out loud, because a collapsed list invites the reading that it
            is advisory. It is not: these are refused server-side on every draft
            and every build, opened or not. */}
        <p className="mt-2 text-xs text-red-900/80" data-testid="denied-enforcement-note">
          These are refused automatically whether or not you open this list — you
          cannot issue a letter that makes one of them.
        </p>
        <ul className="mt-3 space-y-3" data-testid="denied-assertions">
          {letterType.denied_assertions.map(id => {
            const spec = deniedById.get(id)
            return (
              <li key={id} className="rounded border border-red-200 bg-white p-3">
                <p className="text-sm font-medium text-red-900">{spec?.label ?? humanise(id)}</p>
                <p className="mt-0.5 font-mono text-[11px] text-red-700">{id}</p>
                {spec ? (
                  <>
                    <p className="mt-2 text-sm text-gray-700">{spec.rationale}</p>
                    {spec.safe_alternative && (
                      <p className="mt-2 rounded border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-sm text-emerald-900">
                        <span className="font-semibold">Instead: </span>
                        {spec.safe_alternative}
                      </p>
                    )}
                    {spec.sources?.length > 0 && (
                      <ul className="mt-2 list-disc pl-5 text-xs text-gray-600">
                        {spec.sources.map(s => <li key={s}>{s}</li>)}
                      </ul>
                    )}
                  </>
                ) : (
                  <p className="mt-2 text-xs text-gray-600">
                    Specific to this letter type. The full rationale and the permitted
                    alternative are shown if a statement trips it.
                  </p>
                )}
              </li>
            )
          })}
        </ul>
      </details>
    </div>
  )
}
